import React from 'react';
import { ChatComposer, IconButton, Alert, Button } from '@parsina/framework';
import { Square, Coins } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { MAX_QUESTION_CHARS, COUNTER_FROM_CHARS } from '../../lib/conversation.js';
import { CostChip } from '../common/ParsehCost.jsx';

// The composer. core.md §9.2.
//
// The kit's `ChatComposer` owns the input behaviour a chat must not re-derive:
// Enter sends, Shift+Enter is a newline, an IME commit is never a send, the
// field auto-grows to 7.5rem and then scrolls, and the field is ≥1rem so iOS
// does not zoom the page on focus. This wrapper adds the three things that are
// THIS app's, not the kit's:
//
//   1. **The price, before the spend.** «۱۰ پارسه» rides in the row, from
//      constants/pricing.js — the same number the credits screen prints.
//   2. **The character counter, after 3 500.** Not before: a counter that is
//      always on turns a question into a form field.
//   3. **«توقف» while an answer is being revealed.** The reveal is animation
//      over a message that is already stored (core.md §8.6), so stopping loses
//      nothing — which is why this is a plain control and not a cancel.
//
// The stop control is drawn OVER the kit's send button rather than replacing
// it: the kit disables send while the field is empty (correct — an empty
// question must not be sendable), and «توقف» has to be pressable exactly then.
//
// `notice` is the closed set from useChat: the inline recovery of §9.5. The
// INSUFFICIENT_CREDITS one is a card with «شارژ» and never a retry, because a
// retry cannot succeed — the balance has not moved (core.md §3.5).

const NOTICE_KEY = {
  invalid: 'errors.invalidInput',
  reserved: 'chat.composer.reserved',
  tooLong: 'chat.composer.tooLong',
  threadGone: 'chat.thread.notFound',
};

export default function Composer({
  value,
  onChange,
  onSend,
  onStop,
  sending = false,
  revealing = false,
  disabled = false,
  offline = false,
  outOfCredits = false,
  notice = null,
  onTopUp,
  focusSignal = 0,
}) {
  // The policy's price once the list has arrived; the constant until then.
  const { costOf } = useCreditPrices();
  const { t, formatNumber } = useTranslation();
  const wrapRef = React.useRef(null);
  const length = String(value || '').length;
  const showCounter = length >= COUNTER_FROM_CHARS;

  // Focus returns to the composer after a send (core.md §9.6). The textarea is
  // found through the DOM rather than a ref prop ON PURPOSE: the kit spreads
  // `inputProps` AFTER its own `ref`, so a ref passed that way replaces the
  // kit's and silently kills the auto-grow it uses that ref for.
  React.useEffect(() => {
    if (!focusSignal) return;
    wrapRef.current?.querySelector('textarea')?.focus();
  }, [focusSignal]);

  return (
    <div ref={wrapRef} data-testid="chat-composer-wrap" style={{ borderTop: `1px solid ${C.border}`, background: C.card }}>
      {notice === 'credits' || outOfCredits ? (
        <Alert
          variant="error"
          title={t('chat.noCredits.title')}
          icon={<Coins size={18} aria-hidden />}
          action={<Button size="sm" onClick={onTopUp}>{t('chat.noCredits.action')}</Button>}
          data-testid="chat-out-of-credits"
          style={{ margin: '10px 16px 0' }}
        >
          {t('chat.noCredits.body')}
        </Alert>
      ) : null}

      {notice && notice !== 'credits' && notice !== 'empty' ? (
        <p
          data-testid="chat-composer-notice"
          role="alert"
          style={{ margin: '10px 16px 0', fontSize: 12.5, color: CT.dangerInk }}
        >
          {t(NOTICE_KEY[notice] || 'errors.generic')}
        </p>
      ) : null}

      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 16px 0', flexWrap: 'wrap',
        }}
      >
        {/* The price of the answer this button will buy — read before it is pressed. */}
        <CostChip cost={costOf('ASK')} before />
        {showCounter ? (
          <span data-testid="chat-counter" style={{ fontSize: 11.5, color: CT.mute }}>
            {t('chat.composer.counter', {
              count: formatNumber(length),
              max: formatNumber(MAX_QUESTION_CHARS),
            })}
          </span>
        ) : null}
        {offline ? (
          <span data-testid="chat-offline" style={{ fontSize: 11.5, color: CT.dangerInk }}>
            {t('chat.composer.offline')}
          </span>
        ) : null}
      </div>

      <div style={{ position: 'relative' }}>
        <ChatComposer
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onSend={() => onSend(value)}
          sending={sending}
          disabled={disabled || offline || outOfCredits}
          maxLength={MAX_QUESTION_CHARS}
          placeholder={t('chat.composer.placeholder')}
          sendLabel={t('chat.composer.send')}
          inputProps={{ 'data-testid': 'chat-input' }}
        />
        {revealing ? (
          <span
            style={{
              position: 'absolute', insetInlineEnd: 16, bottom: 8,
              background: C.card, borderRadius: 999,
            }}
          >
            <IconButton
              icon={<Square size={16} aria-hidden />}
              label={t('chat.composer.stop')}
              onClick={onStop}
              data-testid="chat-stop"
            />
          </span>
        ) : null}
      </div>
    </div>
  );
}
